import { QueryClient, QueryClientProvider } from 'react-query'

import { ScreenClassProvider, SWAN_STYLE_KEY_MAP } from '@vp/swan'
import type { ProductOptionsConfiguratorData } from '@vp/product-options'
import { IdentityProvider, useLogger, useStyles, useSwanStyleKeys, useTenantMappings, useUserContext } from '@vp/ubik-context'
import { LocalizationsProvider, type NestedStringObject } from '@vp/ubik-localizations-provider'
import { SWAN_STYLE_KEYS as PRODUCT_OPTIONS_STYLE_KEYS } from '@vp/product-options'

import { QuantityPageWrapper } from './quantity/addToCartWrapper/QuantityPageWrapper'
import { AuthorizationProvider } from './root/contexts/AuthorizationProvider'
import { PricingContextProvider } from './root/contexts/PricingContext'
import { AppContextProvider } from './root/contexts/AppContext'
import { ABTestContextProvider } from './root/contexts/ABTestContext'
import { parseQueryParameters } from './root/contexts/AppContext/Parameters/parseParameters'
import { stylesheet as quantityStyles } from './quantity/quantity.scss'
import { getCountryFromLocale } from '../utils/LocalizationHelpers'
import { TenantError } from './errorPage/TenantError'

export interface Props {
  authorizationHeader?: string
  localizations: NestedStringObject
  configuratorData?: ProductOptionsConfiguratorData
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
})

export const Fragment = (props: Props) => {
  const logger = useLogger()
  const userContext = useUserContext()
  const { tenant } = userContext
  const { pricing: pricingTenant } = useTenantMappings()
  logger.log('Rendering fragment for a locale', userContext.locale)

  useStyles(quantityStyles)
  useSwanStyleKeys(
    Array.from(
      new Set([
        ...PRODUCT_OPTIONS_STYLE_KEYS,
        SWAN_STYLE_KEY_MAP.accordion,
        SWAN_STYLE_KEY_MAP.alertBox,
        SWAN_STYLE_KEY_MAP.button,
        SWAN_STYLE_KEY_MAP.carousel,
        SWAN_STYLE_KEY_MAP.core,
        SWAN_STYLE_KEY_MAP.grid,
        SWAN_STYLE_KEY_MAP.hidden,
        SWAN_STYLE_KEY_MAP.icon,
        SWAN_STYLE_KEY_MAP.modalDialog,
        SWAN_STYLE_KEY_MAP.progressiveImage,
        SWAN_STYLE_KEY_MAP.standardTile,
        SWAN_STYLE_KEY_MAP.table,
        SWAN_STYLE_KEY_MAP.toggleSwitch,
        SWAN_STYLE_KEY_MAP.visible,
        SWAN_STYLE_KEY_MAP.utility,
        SWAN_STYLE_KEY_MAP.spinner,
        SWAN_STYLE_KEY_MAP.popover,
        SWAN_STYLE_KEY_MAP.pricing,
      ]),
    ),
  )

  if (!tenant) {
    const errorPage = (props.localizations['error-page'] ?? {}) as Record<string, string>
    return <TenantError heading={errorPage['error-heading'] ?? 'An error occurred.'} subtitle={errorPage['error-subtitle'] ?? 'Please try again later.'} />
  }

  // const workId = userContext.queryStrings?.['workid']

  const createAuthConfig = (culture: string, isDevelopment = false) => ({
    culture,
    developmentMode: isDevelopment,
    options: {
      requireSession: true,
      customText: 'Sign in to see your account',
    },
  })

  return (
    <ScreenClassProvider>
      <IdentityProvider auth={createAuthConfig(userContext.locale)}>
        <AuthorizationProvider authorizationHeader={props.authorizationHeader}>
          <PricingContextProvider tenant={pricingTenant} market={getCountryFromLocale(userContext.locale)}>
            <QueryClientProvider client={queryClient}>
              <AppContextProvider queryParameters={parseQueryParameters()} locale={userContext.locale}>
                <LocalizationsProvider localizations={props.localizations} logger={logger}>
                  <ABTestContextProvider>
                    <div className="post-design-configuration">
                      <div className="quantity-app">
                        <QuantityPageWrapper configuratorData={props.configuratorData} />
                      </div>
                    </div>
                  </ABTestContextProvider>
                </LocalizationsProvider>
              </AppContextProvider>
            </QueryClientProvider>
          </PricingContextProvider>
        </AuthorizationProvider>
      </IdentityProvider>
    </ScreenClassProvider>
  )
}
